import React, { useState, useEffect } from "react";
import { Box, BoxProps } from "@mui/material";

// 로드·색상 변환된 SVG 마크업을 (경로|색상) 단위로 캐시한다.
// 동일 아이콘 재마운트(탭 전환 등) 시 비동기 fetch 없이 즉시 렌더해
// 빈 아이콘 깜빡임·레이아웃 시프트를 방지한다.
const svgCache = new Map<string, string>();

export interface IconProps extends Omit<BoxProps, "children"> {
  /**
   * 아이콘 경로 (크기별 디렉토리 포함)
   * 예: "arrow/left", "etc/edit", "tool/line", "logo/icon"
   */
  name: string;
  /**
   * 아이콘 크기 (12, 16, 20, 24 중 하나)
   * logo 디렉토리의 아이콘은 크기 무시
   */
  size?: 12 | 16 | 20 | 24;
  /**
   * 아이콘 색상
   */
  color?: string;
  /**
   * 클릭 가능한지 여부
   */
  clickable?: boolean;
}

/**
 * Icon 컴포넌트
 *
 * public/icons 폴더의 SVG 아이콘들을 렌더링하는 컴포넌트입니다.
 * 다양한 크기와 색상을 지원하며, 클릭 가능한 아이콘으로도 사용할 수 있습니다.
 *
 * 지원하는 아이콘 경로:
 * - 크기별 아이콘: "arrow/left", "etc/edit", "tool/line", "display/3d-line", "nav/2d3d"
 * - 로고 아이콘: "logo/icon", "logo/main", "logo/name"
 * - 카드 이미지: "card-image/large-enable-card-image"
 */
export const Icon: React.FC<IconProps> = ({
  name,
  size = 24,
  color = "currentColor",
  clickable = false,
  sx,
  ...props
}) => {
  const iconPath = `/icons/${name}.svg`;
  const cacheKey = `${iconPath}|${color}`;
  const cached = svgCache.get(cacheKey);
  const [svgContent, setSvgContent] = useState<string>(cached ?? "");
  const [loading, setLoading] = useState(cached === undefined);

  // SVG 파일을 로드하고 색상을 적용
  useEffect(() => {
    const hit = svgCache.get(cacheKey);
    if (hit !== undefined) {
      setSvgContent(hit);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const loadSvg = async () => {
      try {
        setLoading(true);
        const response = await fetch(iconPath);
        if (response.ok) {
          let svgText = await response.text();

          // 색상이 currentColor가 아닌 경우 SVG의 fill과 stroke를 변경
          if (color !== "currentColor") {
            const isArrow = name.toLowerCase().includes("arrow");

            if (isArrow) {
              // arrow 아이콘은 stroke만 color 반영
              svgText = svgText.replace(/stroke="[^"]*"/g, `stroke="${color}"`);
              svgText = svgText.replace(
                /stroke-opacity="[^"]*"/g,
                'stroke-opacity="1"'
              );
            } else {
              // arrow가 아닌 아이콘은 fill과 stroke 모두 반영
              // 단, fill="none"인 경우는 유지하고 stroke만 변경
              svgText = svgText.replace(/stroke="[^"]*"/g, `stroke="${color}"`);

              // fill="none"이 아닌 경우에만 fill 변경
              svgText = svgText.replace(
                /fill="(?!none)[^"]*"/g,
                `fill="${color}"`
              );

              // fill-opacity나 stroke-opacity가 있는 경우도 처리
              svgText = svgText.replace(
                /fill-opacity="[^"]*"/g,
                'fill-opacity="1"'
              );
              svgText = svgText.replace(
                /stroke-opacity="[^"]*"/g,
                'stroke-opacity="1"'
              );
            }
          }

          svgCache.set(cacheKey, svgText);
          if (!cancelled) setSvgContent(svgText);
        }
      } catch (error) {
        console.error("Failed to load SVG:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSvg();
    return () => {
      cancelled = true;
    };
  }, [iconPath, color, cacheKey]);

  if (loading) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...sx,
        }}
        {...props}
      />
    );
  }

  return (
    <Box
      component="div"
      sx={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: clickable ? "pointer" : "default",
        transition: "opacity 0.2s ease-in-out",
        "&:hover": clickable
          ? {
              opacity: 0.7,
            }
          : {},
        "& svg": {
          width: "100%",
          height: "100%",
        },
        ...sx,
      }}
      {...props}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

// named export만 사용 (default export 제거)
